import React, { useMemo, useState, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import TranscriptBlock from './TranscriptBlock';
import TranscriptBlockPlaceholder from './TranscriptBlockPlaceholder';
import BlockInsertZone from './BlockInsertZone';

const SLATE_MARGIN = 2;

/**
 * TranscriptBlocks Container Component
 * 
 * Container for all transcript blocks with virtualization support
 */
const TranscriptBlocks = ({
  blocks = [],
  speakers = [],
  transcriptLoading = false,
  currentBlockId,
  focusedBlock,
  selectedBlocks = [],
  onTextChange,
  onSpeakerChange,
  onFocusBlock,
  onBlurBlock,
  onSelectBlock,
  onSplit,
  onMergeUp,
  onMergeDown,
  onInsertBlock,
  onDelete,
  onDuplicate,
  onSeekToBlock,
  onSeekTo,
  onPlay,
  currentTime,
  isPlaying = false,
  readOnly = false,
  className = '',
  scrollParentRef = null,
}) => {
  // Safety check: ensure blocks is always an array
  const safeBlocks = Array.isArray(blocks) ? blocks : [];

  // Enable virtualization for transcripts with 50+ blocks (reduces DOM nodes and memory)
  const shouldVirtualize = safeBlocks.length >= 50;

  const isSelectedSet = useMemo(() => new Set(selectedBlocks), [selectedBlocks]);

  const [visibleRange, setVisibleRange] = useState(null);
  const handleRangeChanged = useCallback((range) => {
    setVisibleRange(range);
  }, []);
  
  return (
    <div
      id="transcript-blocks-container"
      className={`space-y-4 ${className}`}
      style={{ scrollMargin: 0, scrollPadding: 0 }}
    >
      {safeBlocks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {transcriptLoading ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-lg font-medium">Loading transcript...</p>
              <p className="text-sm mt-2">Fetching transcript data in the background</p>
            </>
          ) : (
            <>
              <p className="text-lg font-medium">No transcript available</p>
              <p className="text-sm mt-2">Generate a transcript to start editing</p>
            </>
          )}
        </div>
      ) : !shouldVirtualize ? (
        safeBlocks.map((block, index) => (
          <React.Fragment key={block.id}>
            <TranscriptBlock
              block={block}
              speakers={speakers}
              isActive={currentBlockId === block.id}
              isFocused={focusedBlock === block.id}
              isSelected={isSelectedSet.has(block.id)}
              onTextChange={onTextChange}
              onSpeakerChange={onSpeakerChange}
              onFocus={onFocusBlock}
              onBlur={onBlurBlock}
              onSelect={onSelectBlock}
              onSplit={onSplit}
              onMergeUp={onMergeUp}
              onMergeDown={onMergeDown}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onSeekToBlock={onSeekToBlock}
              onSeekTo={onSeekTo}
              onPlay={onPlay}
              currentTime={currentBlockId === block.id ? currentTime : null}
              isPlaying={isPlaying}
              readOnly={readOnly}
            />
            {/* Insert zone between blocks */}
            {index < safeBlocks.length - 1 && (
              <BlockInsertZone
                afterBlockId={block.id}
                onInsert={onInsertBlock}
                readOnly={readOnly}
              />
            )}
          </React.Fragment>
        ))
      ) : (
        <Virtuoso
          customScrollParent={scrollParentRef?.current || undefined}
          data={safeBlocks}
          rangeChanged={handleRangeChanged}
          itemContent={(index, block) => {
            const isActive = currentBlockId === block.id;
            const isFocused = focusedBlock === block.id;
            const isSelected = isSelectedSet.has(block.id);

            const isInCoreRange =
              visibleRange &&
              index >= visibleRange.startIndex + SLATE_MARGIN &&
              index <= visibleRange.endIndex - SLATE_MARGIN;

            const useFullSlate =
              !visibleRange ||
              isInCoreRange ||
              block.id === currentBlockId ||
              block.id === focusedBlock;

            return (
              <div className="space-y-4">
                {useFullSlate ? (
                  <TranscriptBlock
                    block={block}
                    speakers={speakers}
                    isActive={isActive}
                    isFocused={isFocused}
                    isSelected={isSelected}
                    onTextChange={onTextChange}
                    onSpeakerChange={onSpeakerChange}
                    onFocus={onFocusBlock}
                    onBlur={onBlurBlock}
                    onSelect={onSelectBlock}
                    onSplit={onSplit}
                    onMergeUp={onMergeUp}
                    onMergeDown={onMergeDown}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onSeekToBlock={onSeekToBlock}
                    onSeekTo={onSeekTo}
                    onPlay={onPlay}
                    currentTime={isActive ? currentTime : null}
                    isPlaying={isPlaying}
                    readOnly={readOnly}
                  />
                ) : (
                  <TranscriptBlockPlaceholder
                    block={block}
                    speakers={speakers}
                    isActive={isActive}
                    isSelected={isSelected}
                    onSeekToBlock={onSeekToBlock}
                    onSeekTo={onSeekTo}
                    onPlay={onPlay}
                    onSelect={onSelectBlock}
                    onFocus={onFocusBlock}
                    isPlaying={isPlaying}
                    readOnly={readOnly}
                  />
                )}
                {index < safeBlocks.length - 1 && (
                  <BlockInsertZone
                    afterBlockId={block.id}
                    onInsert={onInsertBlock}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default TranscriptBlocks;


